import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Card from '../../../components/ui/Card';
import Icon from '../../../components/AppIcon';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const LeadSourceChart = ({ data = [], aiAnalysis = null, loading = false }) => {
  return (
    <div className="space-y-6">
      {/* AI Lead Performance Analysis */}
      {aiAnalysis && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Icon name="Brain" className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">OpenAI Lead Performance Analysis</h3>
              <p className="text-sm text-muted-foreground">GPT-5 Sales Optimization Insights</p>
            </div>
          </div>

          {/* Conversion Rates */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="TrendingUp" className="w-4 h-4" />
              Conversion Rates by Stage
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {aiAnalysis?.conversionRates?.inquiry?.toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground">Inquiry</div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {aiAnalysis?.conversionRates?.siteVisit?.toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground">Site Visit</div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {aiAnalysis?.conversionRates?.proposal?.toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground">Proposal</div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {aiAnalysis?.conversionRates?.negotiation?.toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground">Negotiation</div>
              </div>
            </div>
          </div>

          {/* Bottlenecks */}
          {aiAnalysis?.bottlenecks?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="AlertCircle" className="w-4 h-4 text-warning-600" />
                Identified Bottlenecks
              </h4>
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
                <ul className="space-y-1.5">
                  {aiAnalysis?.bottlenecks?.map((bottleneck, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-warning-800">
                      <Icon name="AlertTriangle" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{bottleneck}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Improvements */}
          {aiAnalysis?.improvements?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="Zap" className="w-4 h-4 text-success-600" />
                AI Improvement Suggestions
              </h4>
              <ul className="space-y-2">
                {aiAnalysis?.improvements?.map((improvement, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                    <Icon name="CheckCircle" className="w-4 h-4 text-success-600 mt-0.5 flex-shrink-0" />
                    <span>{improvement}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Expected Impact */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Target" className="w-4 h-4" />
              Expected Impact
            </h4>
            <p className="text-sm text-muted-foreground">
              {aiAnalysis?.expectedImpact}
            </p>
          </div>
        </Card>
      )}
      {loading && !aiAnalysis && (
        <Card className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Analyzing lead performance with OpenAI...</p>
            </div>
          </div>
        </Card>
      )}
      {/* Lead Source Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Lead Source Performance</h3>
        {data?.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="count"
              >
                {data?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="Users" className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No lead source data available</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default LeadSourceChart;